// ── Theme types & presets ───────────────────────────────────────────

export interface ThemeColors {
  primary: string;
  background: string;
  surface: string;
  text: string;
  border: string;
}

export const DEFAULT_THEME: ThemeColors = {
  primary: '#5F3DC4',
  background: '#F4F3F8',
  surface: '#FFFFFF',
  text: '#1C2033',
  border: '#E8E6F0',
};

export const THEME_PRESETS: Record<string, ThemeColors> = {
  default: DEFAULT_THEME,
  snaggDark: { primary: '#9F7BD4', background: '#1C2033', surface: '#252A42', text: '#F4F3F8', border: '#3A3F5A' },
  midnight: { primary: '#7C6AFF', background: '#0D0D1E', surface: '#1A1A30', text: '#E8E5F0', border: '#2E2E4A' },
  sunset: { primary: '#FF6B35', background: '#FFF8F3', surface: '#FFFFFF', text: '#1A0E05', border: '#E0D0C2' },
  forest: { primary: '#2D9D5C', background: '#F2F8F4', surface: '#FFFFFF', text: '#0D1F12', border: '#C2D6CA' },
  snaggPinkDark: { primary: '#FF2E63', background: '#0D0D0D', surface: '#1A1A1A', text: '#F2F2F7', border: '#2A2A2A' },
  snaggPinkLight: { primary: '#FF2E63', background: '#FFF0F3', surface: '#FFFFFF', text: '#1A0A10', border: '#E8C2CC' },
};

// ── Color mixing helpers ────────────────────────────────────────────

function parseHex(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

function toHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${clamp(r).toString(16).padStart(2, '0')}${clamp(g).toString(16).padStart(2, '0')}${clamp(b).toString(16).padStart(2, '0')}`.toUpperCase();
}

/** Mix c1 toward c2 by ratio (0 = pure c1, 1 = pure c2). */
function mixColors(c1: string, c2: string, ratio: number): string {
  const [r1, g1, b1] = parseHex(c1);
  const [r2, g2, b2] = parseHex(c2);
  return toHex(
    r1 + (r2 - r1) * ratio,
    g1 + (g2 - g1) * ratio,
    b1 + (b2 - b1) * ratio,
  );
}

/** Return an rgba() string for the given hex at the given alpha (0–1). */
function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ── Apply theme to CSS variables ────────────────────────────────────

export function applyTheme(theme: ThemeColors) {
  const root = document.documentElement.style;

  // Base colors
  root.setProperty('--color-primary', theme.primary);
  root.setProperty('--color-bg', theme.background);
  root.setProperty('--color-surface', theme.surface);
  root.setProperty('--color-text', theme.text);
  root.setProperty('--color-border', theme.border);

  // Derived text shades (mix text toward surface)
  root.setProperty('--color-text-bold', mixColors(theme.text, theme.surface, 0.11));
  root.setProperty('--color-text-emphasized', mixColors(theme.text, theme.surface, 0.28));
  root.setProperty('--color-text-secondary', mixColors(theme.text, theme.surface, 0.39));
  root.setProperty('--color-text-subtle', mixColors(theme.text, theme.surface, 0.68));

  // Derived border
  root.setProperty('--color-border-light', mixColors(theme.border, theme.surface, 0.40));

  // Primary tint variants (mix primary toward surface)
  root.setProperty('--color-primary-mid', mixColors(theme.primary, theme.surface, 0.35));
  root.setProperty('--color-primary-light', '#e462de');

  // Primary rose: shift primary toward hot pink, then lighten slightly
  const hotPink = '#FF1A75';
  const rose = mixColors(theme.primary, hotPink, 0.65);
  root.setProperty('--color-primary-rose', mixColors(rose, theme.surface, 0.10));

  // Primary alpha variants
  root.setProperty('--color-primary-08', withAlpha(theme.primary, 0.03));
  root.setProperty('--color-primary-10', withAlpha(theme.primary, 0.06));
  root.setProperty('--color-primary-15', withAlpha(theme.primary, 0.08));
  root.setProperty('--color-primary-20', withAlpha(theme.primary, 0.13));
  root.setProperty('--color-primary-30', withAlpha(theme.primary, 0.19));
}

export function themeFromConfig(json: string | null): ThemeColors {
  if (!json) return DEFAULT_THEME;
  try {
    const parsed = JSON.parse(json);
    if (parsed.primary && parsed.background && parsed.surface && parsed.text && parsed.border) {
      return parsed as ThemeColors;
    }
    return DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

// ── Design-system color tokens (CSS variable backed) ────────────────

export const colors = {
  brand: {
    blue: 'var(--color-primary, #5F3DC4)',
    mid: 'var(--color-primary-mid)',
    light: 'var(--color-primary-light)',
    rose: 'var(--color-primary-rose)',
    blue08: 'var(--color-primary-08)',
    blue10: 'var(--color-primary-10)',
    blue15: 'var(--color-primary-15)',
    blue20: 'var(--color-primary-20)',
    blue30: 'var(--color-primary-30)',
  },
  accent: {
    pink: '#FF2D55',
    purple: '#5F3DC4',
  },
  ink: {
    black: 'var(--color-text, #1C2033)',
  },
  gray: {
    100: 'var(--color-bg, #F4F3F8)',
    200: 'var(--color-border-light, #F1F0F6)',
    300: 'var(--color-border, #E8E6F0)',
    400: 'var(--color-text-subtle, #B6B8BE)',
    600: 'var(--color-text-secondary, #757783)',
    700: 'var(--color-text-emphasized, #5C5E6C)',
    900: 'var(--color-text-bold, #353949)',
  },
  white: 'var(--color-surface, #FFFFFF)',
  status: {
    success: '#22C55E',
    error: '#DC2626',
    warning: '#F59E0B',
    info: '#3B82F6',
  },
};
